import { useContext, useRef } from 'react';
import ThreeDotsPopup from 'modules/core/components/ThreeDotsPopup';
import { PopupContext } from '../components/PopupContext';

export interface BlockingProcessOptions {
  /** title to display in blocking UX */
  title: string;
  /** subtitle for the blocking UX */
  subtitle?: string;
  /** how long to delay the blocking UX in ms, helpful for processes with unknown timing. */
  delayUXBlocking?: number;
}

export type ProcessAndBlockFunction<T> = (process: () => Promise<T>, options: BlockingProcessOptions) => Promise<T>;

/**
 * useBlockingProcess is a convenience hook that will allow you to track an async process with a blocking UI.
 *
 * @returns a process function to process the async action
 */
const useBlockingProcess = () => {
  const popupContext = useContext(PopupContext);
  const threeDotsLoaderRef = useRef<HTMLDivElement>(null);

  function processWithThreeDots<T>(process: () => Promise<T>, options: BlockingProcessOptions) {
    return new Promise<T>((resolve, reject) => {
      // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
      let showPopupTimeout: NodeJS.Timeout = null;
      const runProcess = async () => {
        try {
          const data = await process();
          if (showPopupTimeout) clearTimeout(showPopupTimeout);
          popupContext.closeAll();
          resolve(data);
        } catch (e) {
          if (showPopupTimeout) clearTimeout(showPopupTimeout);
          popupContext.closeAll();
          reject(e);
        }
      };

      const processPromise = runProcess();

      const displayPopup = () => {
        popupContext.display(
          <ThreeDotsPopup
            firstFocusRef={threeDotsLoaderRef}
            title={options.title}
            subTitle={options.subtitle}
            process={() => processPromise}
          />,
          {
            blocking: true,
            modelOptions: {
              initialFocusRef: threeDotsLoaderRef,
            },
          }
        );
      };

      if (options.delayUXBlocking) {
        showPopupTimeout = setTimeout(displayPopup, options.delayUXBlocking);
      } else {
        displayPopup();
      }
    });
  }

  return processWithThreeDots;
};

export default useBlockingProcess;
